import { LitElement, css, html } from 'lit'

export default class StylableSelect extends LitElement {
  static properties = {
    options: { type: Array },
    value: { type: String },
    placeholder: { type: String },
    disabled: { type: Boolean },
    _isOpen: { state: true },
    _focusedIndex: { state: true }
  }

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      --select-border-top: 1px solid var(--foreground);
    }

    .select-container {
      position: relative;
      width: 100%;
    }

    .select-trigger {
      width: 100%;
      border-top: var(--select-border-top, 1px solid var(--color-gt-black));
      border-right: var(--select-border-right, none);
      border-bottom: var(--select-border-bottom, none);
      border-left: var(--select-border-left, none);
      background: var(--select-background, transparent);
      padding: var(--select-padding, 16px 0);
      font-size: var(--select-font-size, 1rem);
      line-height: var(--select-line-height, 1.625);
      font-family: var(--select-font-family, inherit);
      color: var(--select-color, inherit);
      cursor: pointer;
      outline: none;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .select-trigger:disabled {
      opacity: var(--select-disabled-opacity, 0.6);
      cursor: not-allowed;
    }

    .select-value {
      flex: 1;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .select-arrow {
      margin-left: 8px;
      pointer-events: none;
      color: var(--select-arrow-color, currentColor);
      transition: transform 0.2s ease;
    }

    .select-trigger[aria-expanded="true"] .select-arrow {
      transform: rotate(180deg);
    }

    .select-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: var(--select-dropdown-z-index, 1000);
      background: var(--select-dropdown-background, light-dark(var(--color-gt-white), var(--color-gt-black)));
      border: none;
      overflow: scroll;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-4px);
      transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease;

      /* Hide scrollbar */
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE and Edge */
    }

    .select-dropdown::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }

    .select-dropdown.open {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    .select-option {
      padding: var(--select-option-padding, 3px 10px);
      font-size: var(--select-option-font-size, 1rem);
      line-height: var(--select-option-line-height, 1.625);
      cursor: pointer;
      border: none;
      border-bottom: 1px solid light-dark(var(--color-gt-black), var(--color-gt-white));
      background: transparent;
      width: 100%;
      text-align: left;
      color: var(--select-option-color, inherit);
      font-family: var(--select-option-font-family, inherit);
    }

    .select-option:last-child {
      border-bottom: none;
    }

    .select-option:hover {
      background: var(--select-option-hover-background, light-dark(var(--color-gt-black), var(--color-gt-white)));
      color: var(--select-option-hover-color, light-dark(var(--color-gt-white), var(--color-gt-black)));
      font-weight: normal;
    }

    .select-option:focus {
      outline: var(--select-option-focus-outline, 2px solid #007acc);
      outline-offset: var(--select-option-focus-outline-offset, -2px);
      background: var(--select-option-focus-background, light-dark(var(--color-gt-black), var(--color-gt-white)));
      color: var(--select-option-focus-color, light-dark(var(--color-gt-white), var(--color-gt-black)));
      font-weight: normal;
    }

    .select-option.selected {
      background: var(--select-option-selected-background, light-dark(var(--color-gt-black), var(--color-gt-white)));
      color: var(--select-option-selected-color, light-dark(var(--color-gt-white), var(--color-gt-black)));
      font-weight: bold;
    }
  `

  constructor() {
    super()
    this.options = []
    this.value = ''
    this.placeholder = 'Select an option'
    this.disabled = false
    this._isOpen = false
    this._focusedIndex = -1
    this._boundDocumentClick = this._handleDocumentClick.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('blur', this._handleBlur.bind(this))
    document.addEventListener('click', this._boundDocumentClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('click', this._boundDocumentClick)
  }

  _handleTriggerClick(e) {
    console.log('Trigger clicked', this._isOpen)
    if (this.disabled) return
    e.stopPropagation()
    this._isOpen = !this._isOpen
    this.requestUpdate()

    if (this._isOpen) {
      this._focusedIndex = this.options.findIndex(option => option.value === this.value)
      if (this._focusedIndex === -1) this._focusedIndex = 0
      this.updateComplete.then(() => {
        const dropdown = this.shadowRoot.querySelector('.select-dropdown')
        if (dropdown) {
          const option = dropdown.querySelector(`[data-index="${this._focusedIndex}"]`)
          option?.focus()
        }
      })
    }
  }

  _handleOptionClick(option) {
    this.value = option.value
    this._isOpen = false
    this.requestUpdate()

    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: option.value, option },
      bubbles: true
    }))
  }

  _handleKeydown(e) {
    if (this.disabled) return

    switch (e.key) {
      case ' ':
      case 'Enter':
        e.preventDefault()
        if (!this._isOpen) {
          this._handleTriggerClick()
        } else if (this._focusedIndex >= 0) {
          this._handleOptionClick(this.options[this._focusedIndex])
        }
        break
      case 'Escape':
        if (this._isOpen) {
          e.preventDefault()
          this._isOpen = false
          this.requestUpdate()
          this.shadowRoot.querySelector('.select-trigger')?.focus()
        }
        break
      case 'ArrowDown':
        e.preventDefault()
        if (!this._isOpen) {
          this._handleTriggerClick()
        } else {
          this._focusedIndex = Math.min(this._focusedIndex + 1, this.options.length - 1)
          this._focusOption()
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (this._isOpen) {
          this._focusedIndex = Math.max(this._focusedIndex - 1, 0)
          this._focusOption()
        }
        break
    }
  }

  _focusOption() {
    this.updateComplete.then(() => {
      const option = this.shadowRoot.querySelector(`[data-index="${this._focusedIndex}"]`)
      option?.focus()
    })
  }

  _handleBlur(e) {
    if (!this.contains(e.relatedTarget)) {
      this._isOpen = false
      this.requestUpdate()
    }
  }

  _handleDocumentClick(e) {
    if (!this.contains(e.target)) {
      this._isOpen = false
      this.requestUpdate()
    }
  }

  render() {
    const selectedOption = this.options.find(option => option.value === this.value)
    const displayValue = selectedOption ? selectedOption.label : this.placeholder

    return html`
      <div class="select-container">
        <div
          class="select-trigger"
          tabindex="${this.disabled ? -1 : 0}"
          role="combobox"
          aria-expanded="${this._isOpen}"
          aria-haspopup="listbox"
          aria-disabled="${this.disabled}"
          @click="${this._handleTriggerClick}"
          @keydown="${this._handleKeydown}"
        >
          <span class="select-value">${displayValue}</span>
          <span class="select-arrow">▼</span>
        </div>

        <div class="select-dropdown ${this._isOpen ? 'open' : ''}" role="listbox">
          ${this.options.map((option, index) => html`
            <button
              class="select-option ${option.value === this.value ? 'selected' : ''}"
              role="option"
              data-index="${index}"
              aria-selected="${option.value === this.value}"
              tabindex="-1"
              @click="${() => this._handleOptionClick(option)}"
            >
              ${option.label}
            </button>
          `)}
        </div>
      </div>
    `
  }
}
